// EL MARCADOR QUE CLAVA UNA PREGUNTA DENTRO DEL DOCUMENTO
//
// Lissette 2026-08-28: *"no me gusta que tenemos que copiar la pregunta allí, y
// segundo se van al final. Solo dame la opción de colocar el box de esas
// opciones donde yo quiera sin necesidad de colocar pregunta"*. Y al
// precisarlo: *"no solo el yes/no, también el check box, las opciones que están
// allí"* — o sea los cinco controles, no solo el Sí/No.
//
// EL PROBLEMA ERA DE MODELO, no de pantalla. Las preguntas se guardaban como
// una LISTA PARALELA al documento (`questions[]`), sin ningún anclaje al texto.
// De ahí salían las dos quejas a la vez:
//   - había que escribir el enunciado, porque el enunciado era lo ÚNICO que
//     identificaba a la pregunta de cara al agente;
//   - y se pintaban en bloque al final, porque sin anclaje no hay otro sitio
//     coherente donde ponerlas.
//
// La pieza que faltaba YA ESTABA EN CASA: el cuerpo de la plantilla es texto
// con marcadores {{...}} que se resuelven al enviar, hay un botón que inserta
// uno justo donde está el cursor, y {{signature}} lleva desde siempre siendo
// "un elemento no textual que el agente coloca donde quiere". Las preguntas
// simplemente no se habían enganchado a ese sistema.
//
// FORMA: {{q:<id>}} — con dos puntos A PROPÓSITO.
//
// El lector de marcadores del editor (`templatePlaceholders`) solo reconoce
// nombres `[a-zA-Z][\w]*`, así que los dos puntos hacen que este marcador le
// resulte INVISIBLE. Importa: si lo viera, lo trataría como un campo sin
// resolver y el modal de envío le pediría al AGENTE que escribiera un valor
// para la pregunta que tiene que contestar el CLIENTE — exactamente al revés.
// Hay una prueba que lo fija.

/** Reconoce {{q:<id>}}. Los ids son los que ya genera el editor (`q_xxx_0`). */
export const MARCADOR_DE_PREGUNTA = /\{\{\s*q:([A-Za-z0-9_-]+)\s*\}\}/g;

/** El marcador de una pregunta, para escribirlo en el cuerpo. */
export function marcadorDe(id) {
  return `{{q:${id}}}`;
}

/** Los ids de las preguntas clavadas en este texto, en el orden en que salen. */
export function idsClavadosEn(texto) {
  const out = [];
  const re = new RegExp(MARCADOR_DE_PREGUNTA.source, 'g');
  let m = re.exec(String(texto || ''));
  while (m) {
    if (!out.includes(m[1])) out.push(m[1]);
    m = re.exec(String(texto || ''));
  }
  return out;
}

/** Quita el marcador de una pregunta (al borrarla, para no dejar el hueco). */
export function quitarMarcador(texto, id) {
  const re = new RegExp(`\\{\\{\\s*q:${String(id).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\}\\}`, 'g');
  return String(texto || '').replace(re, '');
}

/**
 * Parte un texto en trozos: los de texto y los que son una pregunta.
 *
 *   [{ tipo: 'texto', valor: '...' }, { tipo: 'pregunta', id: 'q_ab' }, ...]
 *
 * Es lo que permite pintar el control EN MEDIO del párrafo en vez de al final.
 * Un marcador de una pregunta que ya no existe se trata como texto vacío: se
 * traga en silencio en vez de enseñarle al cliente `{{q:q_ab}}` en mitad de su
 * documento, que es lo que pasaría dejándolo pasar tal cual.
 */
export function partirPorPreguntas(texto, idsValidos) {
  const validos = idsValidos ? new Set(idsValidos) : null;
  const fuente = String(texto || '');
  const trozos = [];
  const re = new RegExp(MARCADOR_DE_PREGUNTA.source, 'g');
  let ultimo = 0;
  let m = re.exec(fuente);
  while (m) {
    if (m.index > ultimo) trozos.push({ tipo: 'texto', valor: fuente.slice(ultimo, m.index) });
    if (!validos || validos.has(m[1])) trozos.push({ tipo: 'pregunta', id: m[1] });
    ultimo = m.index + m[0].length;
    m = re.exec(fuente);
  }
  if (ultimo < fuente.length) trozos.push({ tipo: 'texto', valor: fuente.slice(ultimo) });
  return trozos;
}

/**
 * Las preguntas que NO están clavadas en el cuerpo, que son las que siguen
 * saliendo en el bloque del final.
 *
 * Existe por las plantillas de ANTES de este cambio —las de Wendy Cercio, de la
 * semana pasada—, que tienen preguntas y ningún marcador. Migrarlas a la fuerza
 * sería adivinar dónde iba cada una dentro de un documento que ya se firmó.
 * Siguen funcionando exactamente igual que hasta ahora.
 */
export function preguntasSueltas(preguntas, cuerpo) {
  const clavados = new Set(idsClavadosEn(cuerpo));
  return (Array.isArray(preguntas) ? preguntas : []).filter((q) => q && !clavados.has(q.id));
}

// ── PARTIR UN DOCUMENTO **CON FORMATO** SIN ROMPERLE EL HTML ────────────────
//
// Desde el 2026-09-16 el cuerpo de una plantilla puede ser HTML (Lissette pidió
// el editor de los correos en Firmas). Partirlo por los marcadores deja trozos
// con las etiquetas a medias —`<div>Texto ` de un lado y ` más</div>` del otro—,
// y un fragmento así no se puede pintar: el navegador (y DOMPurify antes que él)
// lo cierra por su cuenta, y el `</div>` suelto del segundo trozo se cae. El
// resultado se ve como un documento descolocado y nadie sabe por qué.
//
// Esto lo arregla cerrando lo que quedó abierto al final de cada trozo y
// volviéndolo a abrir al principio del siguiente, con las MISMAS etiquetas.
//
// Y el detalle que hace que la pregunta siga saliendo DONDE la pusieron: las
// piezas de un bloque partido se reabren `display:inline`. Sin eso, cada trozo
// sería un bloque propio y el control caería en su propia línea — que es
// exactamente lo que Lissette pidió quitar (2026-08-28: *"solo dame la opción de
// colocar el box donde yo quiera"*).

/** Etiquetas sin cierre: no entran en la pila. */
const SIN_CIERRE = /^(br|img|hr|wbr|input|source|col|area|base|link|meta|param|track|embed)$/i;

/** ¿Este nombre de etiqueta es un bloque? Los bloques partidos se reabren en
 *  línea para que la pregunta no se vaya de renglón. */
const ES_BLOQUE = /^(p|div|h[1-6]|li|blockquote|section|article|pre|td|tr|ul|ol|table|tbody)$/i;

/** La etiqueta de apertura, forzada a `display:inline` si es un bloque. */
function reabrir({ nombre, abre }) {
  if (!ES_BLOQUE.test(nombre)) return abre;
  if (/style\s*=\s*"/i.test(abre)) return abre.replace(/style\s*=\s*"/i, 'style="display:inline;');
  if (/style\s*=\s*'/i.test(abre)) return abre.replace(/style\s*=\s*'/i, "style='display:inline;");
  return abre.replace(/^<\s*([a-z0-9]+)/i, '<$1 style="display:inline"');
}

/**
 * Los trozos de `partirPorPreguntas`, con el HTML de cada uno ya equilibrado.
 *
 * Los de pregunta se devuelven tal cual. Un cuerpo sin etiquetas no se toca (no
 * hay nada que equilibrar), así que llamar a esto con texto plano es inofensivo.
 */
export function equilibrarTrozosHtml(trozos) {
  const pila = [];
  return (Array.isArray(trozos) ? trozos : []).map((t) => {
    if (!t || t.tipo !== 'texto') return t;
    const apertura = pila.map(reabrir).join('');
    let cuerpo = t.valor;
    const re = /<[^>]+>/g;
    let m = re.exec(cuerpo);
    while (m) {
      const etiqueta = m[0];
      const nombre = (/^<\s*\/?\s*([a-z0-9]+)/i.exec(etiqueta) || [])[1];
      if (nombre && !SIN_CIERRE.test(nombre) && !/\/>\s*$/.test(etiqueta)) {
        if (/^<\s*\//.test(etiqueta)) {
          for (let i = pila.length - 1; i >= 0; i -= 1) {
            if (pila[i].nombre.toLowerCase() === nombre.toLowerCase()) { pila.splice(i, 1); break; }
          }
        } else {
          // Se guarda DONDE se abrio dentro de este trozo: si al final del trozo
          // sigue abierta, es que la pregunta la parte por la mitad y hay que
          // volver atras a ponerla en linea (ver abajo).
          pila.push({ nombre, abre: etiqueta, desde: m.index });
        }
      }
      m = re.exec(cuerpo);
    }
    // LO QUE QUEDA ABIERTO SE PARTE, asi que su apertura —la de ESTE trozo— tiene
    // que ser en linea tambien. Sin esto, el texto de ANTES de la pregunta se
    // quedaba en su propio renglon y el de despues junto al control: la pregunta
    // partia el parrafo en dos por arriba y no por abajo, que se ve raro y no es
    // lo que la agente escribio.
    //
    // Se parchea de atras hacia delante para que los indices no se muevan.
    const abiertasAqui = pila.filter((x) => typeof x.desde === 'number').sort((a, b) => b.desde - a.desde);
    for (const x of abiertasAqui) {
      const enLinea = reabrir(x);
      if (enLinea !== x.abre) {
        cuerpo = cuerpo.slice(0, x.desde) + enLinea + cuerpo.slice(x.desde + x.abre.length);
      }
      delete x.desde; // ya no vive en este trozo: en el siguiente se reabre entera
    }
    const cierre = [...pila].reverse().map((x) => `</${x.nombre}>`).join('');
    return { ...t, valor: apertura + cuerpo + cierre };
  });
}
